import React, { useState, useEffect, useCallback } from 'react';
import { Badge, STATUS_COLORS, CIRCLE_NUMS, STRING_COLORS_LIGHT } from '../../../types/fingeringEditor';
import { getNoteFromStringFret, getFretForNoteOnString, getMidiForStringFret } from '../../../utils/guitarTuning';
import FretScroller from './FretScroller';

function computeNewStatus(
  badge: Badge,
  editString: number | null,
  editFret: number | null,
): Badge['status'] {
  return badge.status === 'auto' && (editString !== badge.string || editFret !== badge.fret)
    ? ('edited' as const)
    : badge.status;
}

interface EditorPanelProps {
  badge: Badge | null;
  onSave: (
    id: number,
    updates: { string: number | null; fret: number | null; note: string; status: Badge['status'] },
    options?: { keepPanelOpen?: boolean },
  ) => void;
  onDelete: (id: number) => void;
  onClose: () => void;
  onAddNoteToBeat?: (beatIndex: number) => void;
  chordSizeForBeat?: (beatIndex: number) => number;
}

const EditorPanel: React.FC<EditorPanelProps> = ({ badge, onSave, onDelete, onClose, onAddNoteToBeat, chordSizeForBeat }) => {
  const [editString, setEditString] = useState<number | null>(null);
  const [editFret, setEditFret] = useState<number | null>(null);
  const [editNote, setEditNote] = useState('');

  useEffect(() => {
    if (badge) {
      setEditString(badge.string);
      setEditFret(badge.fret);
      setEditNote(badge.note || '');
    }
  }, [badge?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editString && editFret !== null) {
      const derived = getNoteFromStringFret(editString, editFret);
      if (derived) setEditNote(derived);
    }
  }, [editString, editFret]);

  // 「同じ音を他の弦で探す」の起点は現在選択中の弦・フレット (順引き)。音名入力窓は使わない。
  const noteMidi: number | null =
    editString != null && editFret !== null ? getMidiForStringFret(editString, editFret) : null;

  // 別の弦をタップ → 同じ音のフレットへ自動ジャンプ (手動での上書きは FretScroller で可能)。
  const handleStringSelect = useCallback((num: number) => {
    setEditString(num);
    if (noteMidi != null) {
      const f = getFretForNoteOnString(noteMidi, num);
      setEditFret(f);
    }
  }, [noteMidi]);

  const isIncomplete = editString == null || editFret == null;

  const handleSave = useCallback(() => {
    if (!badge || isIncomplete) return;
    const newStatus = computeNewStatus(badge, editString, editFret);
    onSave(badge.id, { string: editString, fret: editFret, note: editNote, status: newStatus });
  }, [badge, editString, editFret, editNote, onSave, isIncomplete]);

  const chordSize = (badge?.beatIndex != null) ? (chordSizeForBeat?.(badge.beatIndex) ?? 1) : 0;
  const canAddNote = !!(badge && badge.beatIndex != null && onAddNoteToBeat);
  const addNoteLabel = chordSize >= 2 ? '＋ 音をさらに追加' : '＋ 音を追加して和音化';

  const handleAddNote = useCallback(() => {
    if (!badge || !onAddNoteToBeat || badge.beatIndex == null || isIncomplete) return;

    const hasUnsavedChanges =
      editString !== badge.string ||
      editFret !== badge.fret ||
      editNote !== (badge.note || '');

    if (hasUnsavedChanges) {
      const newStatus = computeNewStatus(badge, editString, editFret);
      onSave(
        badge.id,
        { string: editString, fret: editFret, note: editNote, status: newStatus },
        { keepPanelOpen: true },
      );
    }

    const beatIndex = badge.beatIndex;
    queueMicrotask(() => onAddNoteToBeat(beatIndex));
  }, [badge, editString, editFret, editNote, onSave, onAddNoteToBeat, isIncomplete]);

  if (!badge) {
    return (
      <div className="text-slate-500 text-center py-5 text-sm">
        バッジをタップして編集
      </div>
    );
  }

  const statusColor = STATUS_COLORS[badge.status];

  return (
    <div className="flex flex-col gap-3.5 md:gap-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: statusColor.bg }} />
          <span className="text-xs text-slate-400 font-medium">{statusColor.label}</span>
        </div>
        <button onClick={() => {
          if (badge && badge.status === 'added' && isIncomplete) {
            onDelete(badge.id);
            return;
          }
          onClose();
        }} className="text-slate-500 hover:text-slate-300 text-xl px-1.5 py-0.5">✕</button>
      </div>

      <div>
        <div className="text-[11px] md:text-xs text-slate-400 font-semibold tracking-wider mb-1.5">弦</div>
        <div className="grid grid-cols-6 gap-1.5 md:gap-2">
          {[...CIRCLE_NUMS].reverse().map((label, i) => {
            const num = 6 - i;
            const active = editString === num;
            // その弦で現在音を出せるフレット (音未選択なら null=判定なし=演奏可)。
            const fret = noteMidi != null ? getFretForNoteOnString(noteMidi, num) : null;
            const canPlay = noteMidi == null || fret != null;
            const strColor = STRING_COLORS_LIGHT[num] ?? '#bb66ff';
            return (
              <button key={num} onClick={() => { if (canPlay) handleStringSelect(num); }} disabled={!canPlay}
                className={`py-2 md:py-3 rounded-xl flex flex-col items-center gap-0.5 text-[15px] md:text-base transition-all
                  ${active ? 'border-2 font-bold'
                    : canPlay ? 'border border-slate-700 bg-slate-800 font-medium hover:border-slate-600'
                    : 'border border-slate-800 bg-slate-800 opacity-35 cursor-default'}`}
                style={active ? { borderColor: strColor, backgroundColor: strColor + '30', color: strColor } : canPlay ? { color: strColor } : undefined}
              >
                <span>{label}</span>
                {fret != null && (
                  <span className="text-[10px] md:text-[11px]" style={active ? { color: strColor } : { color: '#64748b' }}>{fret}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-1.5">
          <span className="text-[11px] md:text-xs text-slate-400 font-semibold tracking-wider">フレット</span>
          {editFret !== null && <span className="text-[13px] text-slate-200 font-bold font-mono">{editFret}</span>}
        </div>
        <FretScroller value={editFret} onChange={(f) => {
          if (f === null) {
            setEditFret(null);
            setEditString(null);
            setEditNote('');
          } else {
            setEditFret(f);
          }
        }} />
      </div>

      {canAddNote && (
        <button
          onClick={handleAddNote}
          disabled={isIncomplete}
          aria-label={addNoteLabel}
          className={`w-full py-2.5 md:py-3 rounded-xl font-semibold text-[13px] md:text-sm transition-all mb-2
            ${isIncomplete
              ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
              : 'border border-brand-green/60 bg-emerald-950/40 text-emerald-300 hover:bg-emerald-900/50 active:scale-[0.98]'
            }`}
        >
          {addNoteLabel}
        </button>
      )}
      <div className="flex gap-2 md:gap-3 mt-0.5">
        <button onClick={handleSave}
          disabled={isIncomplete}
          className={`flex-1 py-3 md:py-3.5 rounded-xl font-bold text-[15px] md:text-base transition-all
            ${isIncomplete
              ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-brand-purple to-indigo-500 text-white shadow-md shadow-purple-500/30 hover:from-purple-700 hover:to-indigo-600 active:scale-[0.98]'
            }`}>
          保存
        </button>
        <button onClick={() => onDelete(badge.id)}
          className="px-4 md:px-5 py-3 md:py-3.5 rounded-xl border border-red-900 bg-red-950/50 text-red-300 font-semibold text-[15px] md:text-base hover:bg-red-900/30 active:scale-[0.98] transition-all">
          削除
        </button>
      </div>
    </div>
  );
};

export default EditorPanel;
